import Vue from 'vue';
import FontFaceObserver from 'fontfaceobserver';
import gsap from 'gsap';
import { CustomEase } from '../gsap-bonus/CustomEase';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper, { Navigation, Pagination } from 'swiper';
import zenScroll from 'zenscroll';

gsap.registerPlugin(CustomEase);
gsap.registerPlugin(ScrollTrigger);

Vue.component('scroll-slider-component', {
    props: {
        slides: {
            type: Array,
            default: () => []
        }
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            swiperSlider: null,
            activeSlide: 0,
            slidesCount: 1,
            slideScrollRange: 1000
        };
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        this.refreshing = false;

        if (viewportWidth >= 768) {
            const font = new FontFaceObserver('AmarelaStencil');

            Vue.nextTick(() => {
                font.load(null, 500).then(() => {
                    gsap.delayedCall(0.2, () => {
                        this.progress = 0;
                        this.initScrollTrigger();
                    });
                }, () => {
                    gsap.delayedCall(0.2, () => {
                        this.progress = 0;
                        this.initScrollTrigger();
                    });
                });
            });
        } else {
            this.initSwiper();
        }

        window.addEventListener('resize', this.resize);
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        resize() {
            this.progress = this.scrollTl.progress();
            ScrollTrigger.getById("trigger1").kill(true);
            this.scrollTl.pause(0).kill(true);

            this.refreshing = true;
            this.initScrollTrigger();
        },

        initScrollTrigger() {
            CustomEase.create("generalEase", "0.45, 0, 0.33, 1");

            this.scrollTl = gsap.timeline({
                scrollTrigger: {
                    id: 'trigger1',
                    trigger: ".scroll-slider",
                    start: "top top",
                    end: `+=${this.slideScrollRange * this.slides.length}`,
                    pin: true,
                    scrub: true
                }
            });

            for (let index = 0; index < this.slides.length; index++) {
                const titles = document.querySelectorAll('.scroll-slider__titles-title');
                const paginationLines = document.querySelectorAll('.scroll-slider__pagination-line');
                const paginationLine = paginationLines[index].querySelector('.scroll-slider__pagination-line-indicator');

                let animationWidth = 0;
                let prevAnimationWidth = 0;

                for (let newIndex = 0; newIndex < index; newIndex++) {
                    animationWidth += titles[newIndex].getBoundingClientRect().width;

                    if (newIndex > 0) {
                        prevAnimationWidth += titles[newIndex - 1].getBoundingClientRect().width;
                    }
                }

                if (index === (this.slides.length - 1)) {
                    this.scrollTl.to('.scroll-slider__titles', {
                        onStart: () => {
                            if (index > 0) {
                                if (!this.refreshing) {
                                    this.activeSlide++;
                                }
                                gsap.to('.scroll-slider__titles', { x: -animationWidth, duration: 0.7, ease: 'generalEase' });
                            }
                        },
                        onReverseComplete: () => {
                            if (index > 0) {
                                if (!this.refreshing) {
                                    this.activeSlide--;
                                }
                                gsap.to('.scroll-slider__titles', { x: -prevAnimationWidth, duration: 0.7, ease: 'generalEase' });
                            }
                        }
                    }, index);

                    this.scrollTl.to(paginationLine, { scaleX: 1, duration: 1, ease: 'none' }, index);
                } else {
                    this.scrollTl.to('.scroll-slider__titles', {
                        duration: 1, ease: 'none',
                        onStart: () => {
                            if (index > 0) {
                                if (!this.refreshing) {
                                    this.activeSlide++;
                                }
                                gsap.to('.scroll-slider__titles', { x: -animationWidth, duration: 0.7, ease: 'generalEase' });
                            }
                        },
                        onReverseComplete: () => {
                            if (index > 1) {
                                if (!this.refreshing) {
                                    this.activeSlide--;
                                }
                                gsap.to('.scroll-slider__titles', { x: -prevAnimationWidth, duration: 0.7, ease: 'generalEase' });
                            }

                            if (index == 1) {
                                if (!this.refreshing) {
                                    this.activeSlide--;
                                }
                                gsap.to('.scroll-slider__titles', { x: 0, duration: 0.7, ease: 'generalEase' });
                            }
                        }
                    }, index);

                    this.scrollTl.to(paginationLine, { scaleX: 1, duration: 1, ease: 'none' }, index);
                }
            }
            // this.refreshing = false;
            this.scrollTl.progress(this.progress);
        },

        skip() {
            let scrollElm = document.getElementById('pagebuilder');
            zenScroll.to(scrollElm, 0)
        },

        initSwiper() {
            this.swiperSlider = new Swiper(this.$refs.slider, {
                modules: [Navigation, Pagination],
                direction: 'horizontal',
                loop: false,
                slidesPerView: 1,
                centeredSlides: false,
                spaceBetween: 10,
                pagination: {
                    el: ".swiper-pagination",
                    type: "progressbar",
                },
                on: {
                    slideChange: function () {
                        var slides = document.querySelector('#numberSlides');
                        slides.innerHTML = (this.activeIndex + 1);
                    },
                    afterInit: () => {
                        var element = document.querySelector('.swiper-pagination-progressbar-fill');
                        var matrix = window.getComputedStyle(element).transform;
                        var matrixArray = matrix.replace("matrix(", "").split(",");
                        var scaleX = parseFloat(matrixArray[0]); // convert from string to number
                        var total = Math.round(1 / scaleX);
                        this.slidesCount = total;
                    },
                    resize: () => {
                        var element = document.querySelector('.swiper-pagination-progressbar-fill');
                        var matrix = window.getComputedStyle(element).transform;
                        var matrixArray = matrix.replace("matrix(", "").split(",");
                        var scaleX = parseFloat(matrixArray[0]); // convert from string to number
                        var total = Math.round(1 / scaleX);
                        this.slidesCount = total;
                    }
                }
            });
        },

        nextSlide() {
            let current = this.swiperSlider.activeIndex;
            this.swiperSlider.slideTo(current + 1);
            this.$el.querySelectorAll('.module__slider-prev')[0].classList.remove('swiper-button-disabled');
            if (this.swiperSlider.isEnd) {
                this.$el.querySelectorAll('.module__slider-next')[0].classList.add('swiper-button-disabled');
            }
        },

        previousSlide() {
            let current = this.swiperSlider.activeIndex;
            this.swiperSlider.slideTo(current - 1);
            this.$el.querySelectorAll('.module__slider-next')[0].classList.remove('swiper-button-disabled');
            if (this.swiperSlider.isBeginning) {
                this.$el.querySelectorAll('.module__slider-prev')[0].classList.add('swiper-button-disabled');
            }
        },
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.resize);

        if (this.scrollTl) {
            this.scrollTl.kill();
            this.scrollTl = null;
        }
    }
});
